import React, { forwardRef, useState } from 'react';
import { Alert, Box, Loader, Slider, Text, type BoxProps } from '@mantine/core';
import {
  IconPlayerPlay,
  IconPlayerPause,
  IconVolume,
  IconVolumeOff,
} from '@tabler/icons-react';
import type { AudioLoadError } from '../../voice';
import { useAudioPlayer } from '../../react';
import {
  MediaToolbarIcon,
  MediaToolbarRow,
} from '../media-toolbar/media-toolbar';

export type AudioPlayerLabels = {
  play: string;
  pause: string;
  mute: string;
  unmute: string;
  loadErrors: Record<AudioLoadError, string>;
};

export interface AudioPlayerProps extends Omit<
  React.ComponentPropsWithoutRef<'audio'>,
  'ref' | 'onVolumeChange'
> {
  /** Заголовок трека */
  title?: string;
  /** Исполнитель */
  artist?: string;
  /** Показывать ли информацию о треке */
  showTrackInfo?: boolean;
  /** Показывать ли элементы управления громкостью */
  showVolumeControls?: boolean;
  /** Показывать ли время воспроизведения */
  showTimeDisplay?: boolean;
  /** Показывать ли прогресс-бар */
  showProgressBar?: boolean;
  /** Пропсы для контейнера */
  containerProps?: BoxProps;
  /** Обработчик изменения состояния воспроизведения */
  onPlayStateChange?: (isPlaying: boolean) => void;
  /** Обработчик изменения прогресса */
  onProgressChange?: (currentTime: number, duration: number) => void;
  /** Обработчик изменения громкости */
  onVolumeChange?: (volume: number) => void;
  labels: AudioPlayerLabels;
}

/**
 * Компонент аудиоплеера с поддержкой Mantine v7
 *
 * Разметка и стили панели управления совпадают с воспроизведением записи в WebmOpusRecorder.
 *
 * @example
 * ```tsx
 * <AudioPlayer
 *   src="/path/to/audio.mp3"
 *   title="Название трека"
 *   artist="Исполнитель"
 *   showTrackInfo
 *   showVolumeControls
 *   showTimeDisplay
 *   onPlayStateChange={(isPlaying) => console.log('Playing:', isPlaying)}
 * />
 * ```
 */
export const AudioPlayer = forwardRef<HTMLAudioElement, AudioPlayerProps>(
  (
    {
      title,
      artist,
      showTrackInfo = true,
      showVolumeControls = true,
      showTimeDisplay = true,
      showProgressBar = true,
      containerProps,
      onPlayStateChange,
      onProgressChange,
      onVolumeChange,
      labels,
      style: audioStyle,
      ...audioProps
    },
    ref
  ) => {
    const [isGrubbing, setIsGrubbing] = useState(false);
    const { preload = 'auto', src, ...restAudioProps } = audioProps;

    const {
      combinedRef,
      isPlaying,
      isDurationLoading,
      volume,
      isMuted,
      loadError,
      progressPercent,
      playbackCurrentLabel,
      playbackTotalLabel,
      togglePlayPause,
      onProgressSliderChange,
      onVolumeSliderChange,
      toggleMute,
    } = useAudioPlayer(ref, {
      src,
      onPlayStateChange,
      onProgressChange,
      onVolumeChangeProp: onVolumeChange,
    });

    const playLabel = isPlaying ? labels.pause : labels.play;
    const muteLabel = isMuted ? labels.unmute : labels.mute;

    const trackLine =
      showTrackInfo && (title || artist)
        ? [title, artist].filter(Boolean).join(' · ')
        : '';

    const showDurationLoader = Boolean(src) && isDurationLoading;

    if (loadError) {
      return (
        <Alert color='gray' variant='outline' radius='md'>
          {labels.loadErrors[loadError]}
        </Alert>
      );
    }

    return (
      <Box
        p={16}
        style={{ borderRadius: 16 }}
        bd='1px solid var(--mantine-color-primary-0)'
        {...containerProps}
      >
        <audio
          ref={combinedRef}
          preload={preload}
          src={src}
          style={{ display: 'none', ...audioStyle }}
          {...restAudioProps}
        >
          <track kind='captions' />
        </audio>

        {trackLine && (
          <Text size='sm' c='tertiary.7' ta='left' truncate mb={8}>
            {trackLine}
          </Text>
        )}

        <MediaToolbarRow>
          <MediaToolbarIcon label={playLabel} onClick={togglePlayPause}>
            {isPlaying ? (
              <IconPlayerPause size={20} />
            ) : (
              <IconPlayerPlay size={20} />
            )}
          </MediaToolbarIcon>

          {showProgressBar && (
            <Slider
              flex={1}
              value={progressPercent}
              onChange={onProgressSliderChange}
              onPointerDown={() => setIsGrubbing(true)}
              onPointerUp={() => setIsGrubbing(false)}
              min={0}
              max={100}
              step={0.1}
              label={playbackCurrentLabel}
              color='blue'
              size='sm'
              disabled={showDurationLoader}
              styles={{
                thumb: {
                  cursor: isGrubbing ? 'grabbing' : 'grab',
                },
                label: {
                  backgroundColor: 'var(--mantine-color-primary-0)',
                  color: 'black',
                },
              }}
            />
          )}

          {showTimeDisplay &&
            (showDurationLoader ? (
              <Loader size='sm' color='blue' style={{ flexShrink: 0 }} />
            ) : (
              <Text
                size='sm'
                c='tertiary.7'
                ta='center'
                style={{
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {playbackCurrentLabel} / {playbackTotalLabel}
              </Text>
            ))}

          {showVolumeControls && (
            <>
              <MediaToolbarIcon label={muteLabel} onClick={toggleMute}>
                {isMuted || volume === 0 ? (
                  <IconVolumeOff size={20} />
                ) : (
                  <IconVolume size={20} />
                )}
              </MediaToolbarIcon>
              <Slider
                w={40}
                value={isMuted ? 0 : volume * 100}
                onChange={onVolumeSliderChange}
                onPointerDown={() => setIsGrubbing(true)}
                onPointerUp={() => setIsGrubbing(false)}
                min={0}
                max={100}
                step={1}
                label={null}
                color='blue'
                size='sm'
                styles={{
                  thumb: {
                    cursor: isGrubbing ? 'grabbing' : 'grab',
                  },
                }}
              />
            </>
          )}
        </MediaToolbarRow>
      </Box>
    );
  }
);

AudioPlayer.displayName = 'AudioPlayer';
